<template>
  <div>
    <FormulateForm
      ref="form"
      name="formOpeningHours"
    >
      <!-- Escopo (definido na listagem): mostrado apenas como contexto, não editável. -->
      <b-card-actions
        no-actions
        :show-loading="loading || busy"
      >
        <div class="d-flex align-items-center">
          <feather-icon
            :icon="scope === 'general' ? 'GlobeIcon' : 'HomeIcon'"
            size="22"
            class="mr-1 text-primary"
          />
          <div>
            <h5 class="mb-0">
              {{ scopeTitle }}
            </h5>
            <small class="text-muted">{{ scopeSubtitle }}</small>
          </div>
        </div>

        <!-- Seleção de loja apenas ao criar um horário novo (loja ainda não escolhida). -->
        <b-row
          v-if="scope === 'store' && !isStoreLocked"
          class="mt-1"
        >
          <b-col md="4">
            <FormulateInput
              id="opening-hours-store"
              v-model="storeModel"
              type="vue-select"
              name="storeId"
              :label="$t('Loja')"
              :options="storeOptions"
              :placeholder="$t('Selecione a loja')"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-card-actions
        v-if="canEdit"
        no-actions
        :show-loading="loading || busy"
      >
        <b-alert
          v-if="scope === 'general'"
          variant="info"
          show
        >
          <div class="alert-body">
            {{ $t('O horário Geral vale para todas as lojas que não possuem horário próprio cadastrado.') }}
          </div>
        </b-alert>

        <b-alert
          v-else-if="!hasOwnHours"
          variant="info"
          show
        >
          <div class="alert-body">
            {{ $t('Esta loja não possui horário próprio e, por isso, usa o horário Geral. Ao salvar qualquer dia abaixo, a loja passa a ter horário próprio e deixa de herdar o Geral.') }}
          </div>
        </b-alert>

        <b-alert
          v-else
          variant="warning"
          show
        >
          <div class="alert-body">
            {{ $t('Esta loja possui horário próprio e ignora o horário Geral por completo. Cadastre todos os dias em que a loja abre; dias em branco são considerados fechados.') }}
          </div>
        </b-alert>

        <opening-hours-week :days="days" />

        <b-row
          v-if="scope === 'store' && hasOwnHours"
          class="mt-1"
        >
          <b-col class="text-right">
            <e-button
              variant="outline-danger"
              :text="$t('Remover horário próprio (usar o Geral)')"
              @click="onClearOwnHours"
            />
          </b-col>
        </b-row>
      </b-card-actions>

      <b-alert
        v-else
        variant="info"
        show
        class="mt-1"
      >
        <div class="alert-body">
          {{ $t('Selecione uma loja para editar o horário de funcionamento.') }}
        </div>
      </b-alert>

      <FAB
        :main-tooltip="$t('Ações')"
        main-icon="keyboard_command_key"
        :actions="actions"
        :fixed-tooltip="true"
        :busy="busy"
        @save="save"
        @cancel="cancel"
      />
    </FormulateForm>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { BRow, BCol, BAlert } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { alerts } from '@/mixins'
import { EButton, FAB } from '@/views/components'
import OpeningHoursWeek from '../components/OpeningHoursWeek.vue'

/**
 * Tela dedicada de "Horários de Funcionamento" (D27, tarefa 16.2), fora do `StoreMaintain` e
 * acessível pelo menu "Lojas".
 *
 * Permite selecionar o escopo — o horário Geral (`StoreId` nulo, template aplicável a todas as
 * lojas sem horário próprio) ou uma loja específica — e editar os sete dias daquele escopo.
 * Consome `GET/PUT /store-opening-hours/general` (Geral) e `GET/PUT /stores/{id}/opening-hours`
 * (loja) pelo módulo Vuex `opening-hours.js`. A resolução do horário efetivo é tudo-ou-nada
 * (próprio-ou-Geral): gravar uma lista vazia no escopo de loja remove os registros próprios e a
 * loja volta a herdar o Geral (D27).
 */
export default {
  components: {
    BRow,
    BCol,
    BAlert,
    BCardActions,
    EButton,
    FAB,
    OpeningHoursWeek,
  },

  mixins: [alerts],

  data() {
    return {
      loading: false,
      busy: false,
      // Loja veio fixada pela listagem (edição): trava o escopo (sem seletor de loja).
      storeLocked: false,
    }
  },

  computed: {
    ...mapState('pages/store/openingHours', ['scope', 'selectedStoreId', 'days', 'hasOwnHours']),
    ...mapGetters('pages/store/openingHours', ['storeOptions']),

    // A loja fica travada (escopo definido pela listagem) quando veio um storeId na navegação.
    isStoreLocked() {
      return this.storeLocked
    },

    selectedStoreName() {
      const found = this.storeOptions.find(option => option.value === this.selectedStoreId)
      return found ? found.label : null
    },

    scopeTitle() {
      if (this.scope === 'general') {
        return this.$t('Horário Geral (todas as lojas)')
      }
      if (this.selectedStoreId) {
        return this.selectedStoreName || this.$t('Horário da loja')
      }
      return this.$t('Novo horário de loja')
    },

    scopeSubtitle() {
      if (this.scope === 'general') {
        return this.$t('Vale para todas as lojas que não têm horário próprio.')
      }
      if (this.selectedStoreId) {
        return this.$t('Horário de funcionamento específico desta loja.')
      }
      return this.$t('Selecione a loja abaixo para começar.')
    },

    storeModel: {
      get() {
        return this.selectedStoreId
      },
      set(value) {
        this.setSelectedStore(value)
        this.onScopeOrStoreChange()
      },
    },

    // Habilita o editor: Geral sempre; loja só depois de selecionada.
    canEdit() {
      return this.scope === 'general' || !!this.selectedStoreId
    },

    actions() {
      return [
        {
          name: 'cancel',
          icon: 'arrow_back',
          color: 'red',
          tooltip: this.$t('Voltar'),
        },
        {
          name: 'save',
          icon: 'save',
          tooltip: this.$t('Salvar'),
        },
      ]
    },
  },

  async mounted() {
    this.loading = true
    try {
      await this.fetchStores()

      // Escopo/loja vêm da tela de listagem via query (?scope=general|store&storeId=123).
      const { scope, storeId } = this.$route.query
      if (scope === 'general' || scope === 'store') {
        this.setScope(scope)
      }
      // Com storeId edita a loja informada e trava o escopo; sem ele (fluxo "novo horário de loja"),
      // zera a seleção para o usuário escolher a loja em vez de reaproveitar a do estado.
      this.storeLocked = !!storeId
      this.setSelectedStore(storeId ? Number(storeId) : null)

      await this.onScopeOrStoreChange()
    } catch (error) {
      this.showGenericError({ error })
    } finally {
      this.loading = false
    }
  },

  beforeDestroy() {
    this.clearOpeningHours()
  },

  methods: {
    ...mapActions('pages/store/openingHours', [
      'setScope',
      'setSelectedStore',
      'fetchStores',
      'fetchGeneralOpeningHours',
      'fetchStoreOpeningHours',
      'saveGeneralOpeningHours',
      'saveStoreOpeningHours',
      'clearOpeningHours',
    ]),

    // Recarrega o horário do escopo/loja atuais.
    async onScopeOrStoreChange() {
      if (!this.canEdit) {
        this.clearOpeningHours()
        return
      }
      this.loading = true
      try {
        if (this.scope === 'store') {
          await this.fetchStoreOpeningHours({ storeId: this.selectedStoreId })
        } else {
          await this.fetchGeneralOpeningHours()
        }
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível carregar o horário de funcionamento') })
      } finally {
        this.loading = false
      }
    },

    // Valida a janela de cada dia: abertura e fechamento devem estar ambos preenchidos ou ambos
    // vazios (dia fechado). O intervalo em si (INVALID_WINDOW) é validado no servidor.
    hasInvalidWindow() {
      return this.days.some(
        day => !!day.openingTime !== !!day.closingTime
      )
    },

    // Remove o horário próprio da loja (salva lista vazia → volta a herdar o Geral, D27), com
    // confirmação. Ao confirmar, persiste e retorna para a listagem.
    async onClearOwnHours() {
      const confirmed = await this.confirm({
        title: this.$t('Remover horário próprio?'),
        text: this.$t('A loja voltará a usar o horário Geral. As configurações próprias serão removidas.'),
        confirmButtonText: this.$t('Sim, remover'),
      })
      if (!confirmed) return

      try {
        this.busy = true
        this.clearOpeningHours()
        await this.saveStoreOpeningHours({ storeId: this.selectedStoreId })
        this.showSuccess({ message: this.$t('Horário próprio removido. A loja agora usa o Geral.') })
        this.$router.push({ name: 'opening-hours-list' })
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível remover o horário próprio') })
      } finally {
        this.busy = false
      }
    },

    async save() {
      this.$refs.form.showErrors()
      if (!this.$refs.form.isValid) {
        this.showInvalidDataMessage()
        return
      }
      if (this.scope === 'store' && !this.selectedStoreId) {
        this.showInvalidDataMessage({ message: this.$t('Selecione uma loja') })
        return
      }
      if (this.hasInvalidWindow()) {
        this.showInvalidDataMessage({
          message: this.$t('Preencha abertura e fechamento juntos, ou deixe ambos vazios para indicar dia fechado.'),
        })
        return
      }

      try {
        this.busy = true
        if (this.scope === 'store') {
          await this.saveStoreOpeningHours({ storeId: this.selectedStoreId })
        } else {
          await this.saveGeneralOpeningHours()
        }
        this.showSuccess({ message: this.$t('Horário de funcionamento salvo com sucesso') })
        await this.onScopeOrStoreChange()
      } catch (error) {
        this.showError({ error, message: this.$t('Não foi possível concluir a operação') })
      } finally {
        this.busy = false
      }
    },

    cancel() {
      this.$router.push({ name: 'opening-hours-list' })
    },
  },
}
</script>

<style></style>
